// ui/trick-resolution-detector.js: M5 Change #10.
//
// Detects per-snapshot trick completions and surfaces the events that
// the audio + particle layers need to fire at the right moment. The
// detector is a small stateful object instantiated once per game mount:
// each detect() call against the latest snapshot returns the trick(s)
// that just resolved since the previous call.
//
// Why detector + caller separation: the audio bus + particle layer are
// side-effecting singletons, and the view layer (NetworkedGameView)
// already owns the snapshot stream. The detector stays pure so it can be
// unit-tested without a DOM or audio engine.
//
// Server contract (game-adapter.js):
//   snapshot.completed_tricks: Array<{
//     winner_seat: number,    // 1..4 (Lua-numbered)
//     winning_card: string,   // e.g. 'JH'
//     plays: Array<{ seat: number, card_id: string }>,
//   }>
// completed_tricks is a Lua table serialized as a JSON object keyed by
// stringified integers; Object.values() before passing in.
//
// Per-hand reset: when a fresh hand starts, completed_tricks length
// drops back to 0. The detector clears its high-water mark so the new
// hand starts fresh.

import { isBower } from '../canvas/cards.js';

/**
 * @typedef {{
 *   winner_seat: number,
 *   winning_card: string,
 *   is_bower: 'left'|'right'|null,
 *   is_loner_sweep: boolean,
 * }} TrickResolutionEvent
 */

export function createTrickResolutionDetector() {
  /** @type {number} */
  let highWaterMark = 0;

  /**
   * @param {{
   *   completed_tricks?: Array<{winner_seat:number, winning_card:string}>,
   *   trump_suit?: string|null,
   *   going_alone?: boolean,
   * }} snapshot
   * @returns {TrickResolutionEvent[]}  newly-resolved tricks (usually 0 or 1)
   */
  function detect(snapshot) {
    if (!snapshot) return [];
    const tricks = Array.isArray(snapshot.completed_tricks)
      ? snapshot.completed_tricks
      : Object.values(snapshot.completed_tricks || {});
    // Hand reset: a snapshot with fewer completed tricks than we last
    // saw means a new hand started. Reset and treat the current count as
    // the new mark so we don't replay history.
    if (tricks.length < highWaterMark) {
      highWaterMark = tricks.length;
    }
    if (tricks.length === highWaterMark) return [];
    const newOnes = tricks.slice(highWaterMark);
    const goingAlone = !!snapshot.going_alone;
    highWaterMark = tricks.length;
    return newOnes.map((t, idx) => {
      const absoluteIdx = (highWaterMark - newOnes.length) + idx;
      const thisIsTrick5 = (absoluteIdx === 4);
      const allFiveWinnersSameTeam = thisIsTrick5 && allWinnersOnSameTeam(tricks);
      return {
        winner_seat: t.winner_seat,
        winning_card: t.winning_card,
        is_bower: isBower(t.winning_card, snapshot.trump_suit ?? null),
        // M5 #15, fires only on the 5th trick of a loner attempt where
        // the maker's team won all 5 tricks. The scoring ceremony shifts
        // from chip burst → fire sweep.
        is_loner_sweep: goingAlone && allFiveWinnersSameTeam,
      };
    });
  }

  function reset() { highWaterMark = 0; }

  return { detect, reset };
}

/**
 * Pure helper: which audio event maps to a trick resolution given the
 * viewer's team. Returns either TRICK_WON_YOURS or TRICK_WON_OPPONENTS.
 *
 * Lua seats 1+3 = team_a, 2+4 = team_b (matches the rest of the codebase).
 *
 * @param {number} winnerSeatLua
 * @param {'team_a'|'team_b'} myTeam
 * @returns {'trick_won_yours'|'trick_won_opponents'}
 */
export function trickWonEventFor(winnerSeatLua, myTeam) {
  const winnerTeam = (winnerSeatLua === 1 || winnerSeatLua === 3) ? 'team_a' : 'team_b';
  return winnerTeam === myTeam ? 'trick_won_yours' : 'trick_won_opponents';
}

/**
 * Lua seat → team helper, kept here so NetworkedGameView doesn't pull it
 * from EndOfHandModal (which has the same logic but as a private fn).
 *
 * @param {number} seatLua  1..4
 * @returns {'team_a'|'team_b'}
 */
export function teamOfSeatLua(seatLua) {
  return (seatLua === 1 || seatLua === 3) ? 'team_a' : 'team_b';
}

/**
 * @param {Array<{winner_seat:number}>} tricks
 * @returns {boolean}
 */
function allWinnersOnSameTeam(tricks) {
  if (!tricks || tricks.length === 0) return false;
  const team0 = teamOfSeatLua(tricks[0].winner_seat);
  for (let i = 1; i < tricks.length; i++) {
    if (teamOfSeatLua(tricks[i].winner_seat) !== team0) return false;
  }
  return true;
}
